import { sql } from "drizzle-orm";
import { boolean, integer, pgTable, text, timestamp, uniqueIndex } from "drizzle-orm/pg-core";

import { productFetchVendorEnum } from "./product-fetch";

/**
 * One vendor category or search page the daily vendor product sourcing cron reads for products
 * nobody has listed yet. Writer: platform staff (`platform.fulfillment.vendorProductSourcing.*`)
 * and the per-stage seed script (`packages/scripts/src/fulfillment/seed-sourcing-rules.ts`);
 * nothing reaches a tenant-facing response.
 *
 * The vendor reuses the product-fetch enum on purpose: a product the sourcing cron takes is
 * fetched through `product_fetches`, so a vendor without a fetch cannot have a sourcing rule
 * either.
 *
 * `tenant_ids` names the tenants each product the rule takes is listed for, one row per tenant;
 * empty means one shared row. It carries no foreign key, so a deleted tenant's id can stay in
 * the array; the save guard refuses unknown ids and the promotion skips them.
 */
export const vendorProductSourcingRules = pgTable(
  "vendor_product_sourcing_rules",
  {
    id: text("id").primaryKey(),
    vendor: productFetchVendorEnum("vendor").notNull(),
    /** The vendor page, stored exactly as staff entered it: its query string is the sort and filter. */
    url: text("url").notNull(),
    /** How many products one run takes from the top of the page that are not listed yet. */
    numNewProducts: integer("num_new_products").notNull().default(5),
    /** Boxed MarketplaceCategory values every listing this rule writes carries. */
    tags: text("tags").array().notNull().default(sql`'{}'::text[]`),
    /** Tenant ids to list each product for; empty means shared across every tenant. */
    tenantIds: text("tenant_ids").array().notNull().default(sql`'{}'::text[]`),
    isEnabled: boolean("is_enabled").notNull().default(false),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [uniqueIndex("vendor_product_sourcing_rules_vendor_url_idx").on(t.vendor, t.url)],
);
